import * as path from "node:path";
import { Command, Option } from "clipanion";
import { DefaultAzureCredential } from "@azure/identity";
import { checkAppHealth, readAppSecrets } from "@m8t-stack/github-app-auth";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { getAzAccount, getCallerObjectId } from "../../lib/auth.js";
import { resolveFoundryProject } from "../../lib/foundry-project.js";
import { getAgentVersion } from "../../lib/foundry-agent-get.js";
import { checkPreconditions } from "../../lib/preconditions.js";
import { deployHostedWorker } from "../../lib/coder-deploy.js";
import { resolvePersona } from "../../lib/persona.js";
import { colors, renderJson, resolveOutputMode } from "../../lib/output.js";
import { discoverKvUri, resolveRepoRoot } from "../../lib/brain-paths.js";
import { resolveAppInstallation } from "../../lib/app-install.js";
import { detectDeliveryEnv, ensureDeliveryGrant, normalizeKvUri } from "../../lib/delivery-grant.js";
import { enableA2a } from "../../lib/a2a-enable.js";
import { resolveBridgeUrl } from "../../lib/bridge-url.js";
import { listModelQuota, modelQuotaVerdict } from "../../lib/quota.js";
import { assertPlatformLive } from "../../lib/platform-live.js";
import { stagePublicImageIfNeeded } from "../../lib/image-staging.js";

const SIZE_PRESETS: Partial<Record<string, { cpu: string; memory: string }>> = {
  small: { cpu: "0.5", memory: "1Gi" },
  medium: { cpu: "1", memory: "2Gi" },
  large: { cpu: "2", memory: "4Gi" },
};
const DEFAULT_REGISTRY = "ghcr.io/m8t-labs";
const DEFAULT_IMAGE = "m8t-coding-agent";
const DEFAULT_TAG = "v0.1.0"; // public GHCR pin (agents/coding-agent/pyproject.toml#version); bump when a new agent version ships
const DEFAULT_MODEL = "gpt-4.1-mini";
const NAME_RE = /^[a-z0-9-]+$/;

export class CoderDeployCommand extends M8tCommand {
  static paths = [["coder", "deploy"]];
  static usage = Command.Usage({
    description: "Deploy the curated coding agent as a hosted Foundry worker.",
    details:
      "Creates a hosted agent version from a container image (defaults to the public GHCR image ghcr.io/m8t-labs/m8t-coding-agent). Post-2026-06-25 Foundry projects require an authenticated pull, so a public image is staged into the customer's Azure Container Registry first (created if missing, then `az acr import` — a server-side copy, no local build) and the agent is deployed from that ACR ref. Re-running with a newer --image-tag imports the new tag and rolls out a new agent version — that is how you update a worker's image. Override --image with a private ACR ref to bring your own registry.",
    examples: [
      ["Deploy a coder with defaults", "$0 coder deploy my-coder"],
      ["Larger sandbox + a custom model", "$0 coder deploy my-coder --size large --model-deployment gpt-4.1"],
      ["Override the exec timeout", "$0 coder deploy my-coder --env M8T_CODER_EXEC_TIMEOUT_SECONDS=300"],
    ],
  });

  name = Option.String();
  persona = Option.String("--persona");
  image = Option.String("--image");
  imageTag = Option.String("--image-tag");
  size = Option.String("--size");
  modelDeployment = Option.String("--model-deployment");
  env = Option.Array("--env");
  endpoint = Option.String("--endpoint");
  subscription = Option.String("--subscription");
  output = Option.String("--output");
  brain = Option.String("--brain");
  brainBranch = Option.String("--branch");
  brainKv = Option.String("--brain-kv");
  allowNonReasoning = Option.Boolean("--allow-non-reasoning", false);
  skipQuotaCheck = Option.Boolean("--skip-quota-check", false);
  gatewayUrl = Option.String("--gateway-url", { description: "Base gateway URL for the a2a bridge (e.g. https://<gateway-fqdn>); enables this coder as an a2a target." });

  async executeCommand(): Promise<number> {
    if (!NAME_RE.test(this.name)) {
      throw new LocalCliError({
        code: "USAGE",
        message: `Invalid worker name '${this.name}'. Use lowercase letters, digits, and hyphens only.`,
        hint: "Example: m8t coder deploy data-coder",
      });
    }

    const size = (this.size ?? "medium").toLowerCase();
    const preset = SIZE_PRESETS[size];
    if (!preset) {
      throw new LocalCliError({
        code: "USAGE",
        message: `Unknown --size '${this.size ?? ""}'. Use one of: small, medium, large.`,
        hint: "small=0.5/1Gi · medium=1/2Gi · large=2/4Gi",
      });
    }

    const persona = this.persona ?? "coding-agent";
    // --image may be a bare repo ("m8t-coding-agent") or a full "host/repo" ref.
    const imageInput = this.image ?? DEFAULT_IMAGE;
    const repoRef = imageInput.includes("/") ? imageInput : `${DEFAULT_REGISTRY}/${imageInput}`;
    const requestedImage = `${repoRef}:${this.imageTag ?? DEFAULT_TAG}`;

    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const interactive = (this.context.stdout as NodeJS.WriteStream).isTTY === true;

    const credential = new DefaultAzureCredential();
    const account = await getAzAccount();
    const subscriptionId = this.subscription ?? account.subscriptionId;
    const callerObjectId = await getCallerObjectId();

    // Footgun guard: refuse if the platform isn't deployed yet (clear message
    // instead of a cryptic unreachable-Foundry failure downstream).
    await assertPlatformLive({ subscriptionId, interactive });

    const project = await resolveFoundryProject({
      credential,
      subscriptionId,
      interactive,
      endpoint: this.endpoint,
    });

    // Merge-preserve (F6): a redeploy must not strip what it doesn't own —
    // metadata like a2a enablement / a brain link, env like delivery + brain vars.
    // First deploy (agent absent) ⇒ nothing to preserve.
    let currentEnv: Partial<Record<string, string>> = {};
    let currentMetadata: Record<string, string> = {};
    try {
      const current = await getAgentVersion({ credential, projectEndpoint: project.endpoint, agentName: this.name });
      const def = current.definition as Record<string, unknown>;
      currentEnv = { ...((def.environment_variables ?? {}) as Record<string, string>) };
      currentMetadata = { ...(current.metadata ?? {}) };
    } catch {
      /* first deploy — nothing to preserve */
    }

    const env: Record<string, string> = {
      ...(currentEnv as Record<string, string>),
      MODEL_DEPLOYMENT_NAME:
        this.modelDeployment ??
        currentEnv.MODEL_DEPLOYMENT_NAME ??
        (typeof this.brain === "string" ? "gpt-5-mini" : DEFAULT_MODEL),
    };
    for (const pair of this.env ?? []) {
      const eq = pair.indexOf("=");
      if (eq <= 0) {
        throw new LocalCliError({
          code: "USAGE",
          message: `Invalid --env '${pair}'. Expected KEY=VALUE.`,
          hint: "Example: --env M8T_CODER_MAX_ITERATIONS=20",
        });
      }
      env[pair.slice(0, eq)] = pair.slice(eq + 1);
    }

    const staged = await stagePublicImageIfNeeded({ image: requestedImage, project });
    for (const n of staged.notes) {
      this.context.stderr.write(`${colors.hint("note:")} ${n}\n`);
    }
    const image = staged.image;

    const { warnings } = await checkPreconditions({
      credential,
      subscriptionId,
      project,
      image,
      callerObjectId,
    });
    for (const w of warnings) {
      this.context.stderr.write(`${colors.hint("note:")} ${w}\n`);
    }

    // `!== true`: run the quota check unless --skip-quota-check was explicitly passed
    // (clipanion's Option descriptor is truthy until argv parse, so `!this.skipQuotaCheck`
    // would skip the check by default under test construction).
    if (this.skipQuotaCheck !== true) {
      const usages = await listModelQuota(project.region);
      const { verdict, quotad } = modelQuotaVerdict(usages, env.MODEL_DEPLOYMENT_NAME);
      if (verdict === "no_quota") {
        throw new LocalCliError({
          code: "MODEL_NO_QUOTA",
          message: `Model '${env.MODEL_DEPLOYMENT_NAME}' has 0 quota in ${project.region}.`,
          hint: `Pick a quota'd model (e.g. --model-deployment ${quotad[0] ?? "gpt-5-mini"}) or request quota. Quota'd reasoning models: ${quotad.join(", ") || "(none)"}. Bypass with --skip-quota-check.`,
        });
      }
      if (verdict === "unknown") {
        this.context.stderr.write(`${colors.hint("note:")} could not confirm quota for '${env.MODEL_DEPLOYMENT_NAME}' in ${project.region}; proceeding.\n`);
      }
    }

    const { persona: personaName, personaVersion } = resolvePersona(persona);

    const onProgress = mode === "pretty" ? (m: string) => this.context.stderr.write(`  ${colors.dim(m)}\n`) : undefined;

    const result = await deployHostedWorker({
      credential,
      subscriptionId,
      project,
      name: this.name,
      image,
      cpu: preset.cpu,
      memory: preset.memory,
      env,
      metadata: { ...currentMetadata, source: "m8t-stack-POC", kind: "hosted", persona: personaName, personaVersion },
      onProgress,
    });

    if (typeof this.brain === "string") {
      const repo = this.brain;
      if (!repo.includes("/")) {
        throw new LocalCliError({ code: "USAGE", message: `--brain must be owner/name, got '${repo}'` });
      }
      const processEnv = this.context.env;
      const kvUri = discoverKvUri(processEnv);
      onProgress?.("checking GitHub App health…");
      const health = await checkAppHealth({ credential, kvUri });
      if (!health.ok) {
        throw new LocalCliError({
          code: "APP_HEALTH_FAILED",
          message: health.transient
            ? "GitHub App health check failed transiently (Key Vault / GitHub API blip)."
            : "GitHub App is not configured correctly.",
          hint: health.transient
            ? "This is usually a transient Azure/GitHub blip — retrying. If it persists, run `m8t brain check-app`."
            : "Run `m8t brain check-app` for details.",
          category: health.transient ? "transient" : "unknown",
          retryable: health.transient === true,
        });
      }
      const { slug, appId, privateKeyPem } = await readAppSecrets({ credential, kvUri });
      const [owner, repoName] = repo.split("/");
      const installationId = await resolveAppInstallation({
        owner, repo: repoName, slug, appId, privateKeyPem,
        write: (s) => this.context.stdout.write(s),
      });
      const { enableHostedBrain } = await import("../../lib/enable-hosted-brain.js");
      await enableHostedBrain({
        credential, subscriptionId, project, kvUri,
        agentName: this.name, repo, branch: typeof this.brainBranch === "string" ? this.brainBranch : "main",
        installationId, modelDeployment: this.modelDeployment ?? "gpt-5-mini",
        // `=== true`: clipanion's Option descriptor is truthy until argv parse, so a
        // bare check would force allowNonReasoning on by default under test construction.
        allowNonReasoning: this.allowNonReasoning === true,
        onProgress,
      });
      this.context.stdout.write(`  ${colors.success("✓")} brain-linked to ${colors.field(repo)} (in-container access; brain version provisioning, active shortly).\n`);
    } else {
      // No --brain, but delivery creds set via raw --env → ensure the KV grant
      // ourselves. Without it the Coder can read its model but NOT the brain
      // token, so it silently delivers nothing (the empty-output trap). Idempotent.
      const delivery = detectDeliveryEnv(env);
      const kvSource = (typeof this.brainKv === "string" ? this.brainKv : undefined) ?? delivery.kvUri;
      if (delivery.installationId && kvSource) {
        const kvUri = normalizeKvUri(kvSource);
        onProgress?.("granting Key Vault Secrets User for delivery…");
        await ensureDeliveryGrant({
          credential,
          subscriptionId,
          principalId: result.principalId,
          kvUri,
        });
        this.context.stdout.write(
          `  ${colors.success("✓")} granted Key Vault Secrets User to the agent identity on ${colors.field(new URL(kvUri).host)} (delivery).\n`,
        );
      }
    }

    try {
      const bridgeUrl = resolveBridgeUrl({
        flag: typeof this.gatewayUrl === "string" ? this.gatewayUrl : undefined,
        env: this.context.env,
      });
      onProgress?.("a2a-enabling as a target…");
      await enableA2a({
        credential,
        projectEndpoint: project.endpoint,
        projectArmId: `${project.accountScope}/projects/${project.projectName}`,
        agentName: this.name,
        personaPath: path.join(resolveRepoRoot(), "personas", personaName, "persona.md"),
        bridgeUrl,
        onProgress,
      });
    } catch (e) {
      if (e instanceof LocalCliError && e.code === "A2A_NO_GATEWAY_URL") {
        onProgress?.("skipping a2a (no gateway URL) — pass --gateway-url to enable.");
      } else {
        throw e;
      }
    }

    if (mode === "json") {
      this.context.stdout.write(
        renderJson({
          name: this.name,
          version: result.version,
          status: result.status,
          persona: personaName,
          image,
          size,
          endpoint: project.endpoint,
          agentPrincipalId: result.principalId,
        }) + "\n",
      );
      return 0;
    }

    this.context.stdout.write(
      `${colors.success("✓")} deployed hosted coder ${colors.field(this.name)} ` +
        `(version ${result.version}, ${result.status}, ${size}, persona ${personaName}).\n`,
    );
    this.context.stdout.write(`  image: ${image}\n`);
    this.context.stdout.write(`  project: ${project.projectName} (${project.region})\n`);
    this.context.stdout.write(
      `  ${colors.hint("next:")} it now appears on MCP (/m8t-stack:workers), the web app, and (when --gateway-url was passed) as an a2a target.\n`,
    );
    return 0;
  }
}
